import { useEffect, useRef, useState } from "react";

export type SlideCreateChoice =
  | { mode: "blank" }
  | { mode: "content"; topic: string | undefined };

type Props = {
  /** 1-based position label shown to the user (where the slide will land). */
  position: number;
  /** Total slides, for the "in coda" hint. */
  total: number;
  /** Source content available → enables the "from content" mode. */
  hasSourceContent: boolean;
  busy?: boolean;
  onCreate: (choice: SlideCreateChoice) => void;
  onClose: () => void;
};

/**
 * Single entry point to add a slide. The blank vs from-content choice is made
 * here (not via separate toolbar buttons). The insert index is decided by the
 * caller (the "+" the user clicked) and rendered as the position label.
 */
export function SlideCreateDialog({
  position,
  total,
  hasSourceContent,
  busy,
  onCreate,
  onClose,
}: Props) {
  const [mode, setMode] = useState<"blank" | "content">(hasSourceContent ? "content" : "blank");
  const [topic, setTopic] = useState("");
  const topicRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (mode === "content") topicRef.current?.focus();
  }, [mode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = () => {
    if (busy) return;
    if (mode === "content") onCreate({ mode: "content", topic: topic.trim() || undefined });
    else onCreate({ mode: "blank" });
  };

  const positionLabel =
    position >= total ? "in coda" : position <= 0 ? "all'inizio" : `in posizione ${position + 1}`;

  return (
    <div className="surf-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="surf-dialog slide-create-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Aggiungi slide"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="surf-dialog-header">
          <h2>Aggiungi slide</h2>
          <span className="surf-dialog-subtitle">Nuova slide {positionLabel}</span>
        </header>

        <div className="slide-create-modes">
          <label className={mode === "blank" ? "slide-create-mode active" : "slide-create-mode"}>
            <input
              type="radio"
              name="slide-create-mode"
              checked={mode === "blank"}
              onChange={() => setMode("blank")}
            />
            <span className="slide-create-mode-title">Slide vuota</span>
            <span className="slide-create-mode-desc">Layout base da riempire a mano.</span>
          </label>

          <label
            className={[
              "slide-create-mode",
              mode === "content" ? "active" : "",
              hasSourceContent ? "" : "disabled",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <input
              type="radio"
              name="slide-create-mode"
              checked={mode === "content"}
              disabled={!hasSourceContent}
              onChange={() => setMode("content")}
            />
            <span className="slide-create-mode-title">Da contenuto (AI)</span>
            <span className="slide-create-mode-desc">
              {hasSourceContent
                ? "Estrae i testi dal contenuto sorgente del deck."
                : "Nessun contenuto sorgente — incollalo nel tab Content."}
            </span>
          </label>
        </div>

        {mode === "content" && (
          <label className="slide-create-topic">
            <span>Argomento (opzionale)</span>
            <textarea
              ref={topicRef}
              rows={2}
              value={topic}
              placeholder="Lascia vuoto per il prossimo tema non ancora coperto…"
              onChange={(e) => setTopic(e.target.value)}
            />
          </label>
        )}

        <footer className="surf-dialog-footer">
          <button type="button" className="surf-btn surf-btn--ghost surf-btn--sm" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="surf-btn surf-btn--sm"
            disabled={busy}
            onClick={submit}
          >
            {busy ? "Creazione…" : "Crea slide"}
          </button>
        </footer>
      </div>
    </div>
  );
}
